import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import React, { useState } from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { FiChevronDown, FiPlus, FiTrash2, FiHelpCircle } from 'react-icons/fi';

// FAQ Item Component
const FAQItemComponent = ({ item, index, onUpdate, onDelete, isEditing }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleQuestionChange = (e) => {
    onUpdate(index, { ...item, question: e.target.value });
  };

  const handleAnswerChange = (e) => {
    onUpdate(index, { ...item, answer: e.target.value });
  };

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        {isEditing ? (
          <input
            type="text"
            value={item.question}
            onChange={handleQuestionChange}
            placeholder="Enter question..."
            onClick={(e) => e.stopPropagation()}
            className="faq-input"
          />
        ) : (
          <span>{item.question || 'Question'}</span>
        )}
        <div className="faq-actions">
          {isEditing && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
              className="faq-delete-btn"
            >
              <FiTrash2 size={14} />
            </button>
          )}
          <FiChevronDown
            size={18}
            className={`faq-chevron ${isOpen ? 'open' : ''}`}
          />
        </div>
      </div>
      {isOpen && (
        <div className="faq-answer">
          {isEditing ? (
            <textarea
              value={item.answer}
              onChange={handleAnswerChange}
              placeholder="Enter answer..."
              className="faq-textarea"
              rows={3}
            />
          ) : (
            <p>{item.answer || 'Answer'}</p>
          )}
        </div>
      )}
    </div>
  );
};

// FAQ Node View Component
const FAQNodeView = ({ node, updateAttributes, selected }) => {
  const [isEditing, setIsEditing] = useState(true);
  const items = node.attrs.items || [];

  const handleUpdateItem = (index, newItem) => {
    const newItems = [...items];
    newItems[index] = newItem;
    updateAttributes({ items: newItems });
  };

  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    updateAttributes({ items: newItems });
  };

  const handleAddItem = () => {
    updateAttributes({
      items: [...items, { question: '', answer: '' }],
    });
  };

  return (
    <NodeViewWrapper className={`faq-block ${selected ? 'selected' : ''}`}>
      <div className="faq-block-header">
        <FiHelpCircle size={18} />
        <span>Frequently Asked Questions</span>
        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          className="faq-edit-toggle"
        >
          {isEditing ? 'Preview' : 'Edit'}
        </button>
      </div>
      <div className="faq-items">
        {items.map((item, index) => (
          <FAQItemComponent
            key={index}
            item={item}
            index={index}
            onUpdate={handleUpdateItem}
            onDelete={handleDeleteItem}
            isEditing={isEditing}
          />
        ))}
      </div>
      {isEditing && (
        <button type="button" onClick={handleAddItem} className="faq-add-btn">
          <FiPlus size={16} />
          <span>Add Question</span>
        </button>
      )}
    </NodeViewWrapper>
  );
};

// FAQ Extension
export const FAQ = Node.create({
  name: 'faq',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      items: {
        default: [
          { question: 'What is this FAQ?', answer: 'This is an example FAQ item.' },
        ],
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="faq"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'faq' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FAQNodeView);
  },

  addCommands() {
    return {
      insertFAQ:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              items: [
                { question: 'What is this FAQ?', answer: 'This is an example FAQ item.' },
              ],
            },
          });
        },
    };
  },
});

export default FAQ;
